/**
 * Builds the copyable prompt that lets a person hand MCP setup to their own AI agent: how to add the server,
 * how to authenticate, how to check it works, and the server's working rules.
 */
export type McpPromptMode =
  { kind: 'personal' } | { kind: 'headless'; agentName: string; clientId?: string | undefined };

export function mcpSetupPrompt(opts: {
  endpoint: string;
  instructions: string;
  mode: McpPromptMode;
}): string {
  const { endpoint, instructions, mode } = opts;
  const origin = new URL(endpoint).origin;
  const add = [
    `Add a remote MCP server named "lixionary-jira" at ${endpoint} (transport: Streamable HTTP).`,
    `- Claude Code: \`claude mcp add --transport http --scope user lixionary-jira ${endpoint}\``,
    `- Codex: add \`[mcp_servers.lixionary-jira]\` with \`url = "${endpoint}"\` to ~/.codex/config.toml`,
    '- Other MCP clients: add it as a remote (Streamable HTTP) server.',
  ];
  const auth =
    mode.kind === 'personal'
      ? [
          'Authenticate with OAuth browser sign-in. The client registers itself and opens a sign-in page;',
          'give me the sign-in link if you cannot open a browser. I will sign in and click Allow.',
          '- Claude Code: run /mcp, pick lixionary-jira and choose Authenticate.',
          '- Codex: run `codex mcp login lixionary-jira`.',
          'You act as my personal agent: you can see and work on the projects I can access, and nothing else.',
        ]
      : [
          `This is the headless agent "${mode.agentName}". It uses OAuth client credentials, not browser sign-in.`,
          `Read the client ID${mode.clientId ? ` (${mode.clientId})` : ''} and secret from the environment variables`,
          'LIXIONARY_JIRA_CLIENT_ID and LIXIONARY_JIRA_CLIENT_SECRET. Never print, log or commit the secret.',
          'Get an access token (valid one hour) and send it as `Authorization: Bearer <token>`:',
          '```sh',
          `curl -s -u "$LIXIONARY_JIRA_CLIENT_ID:$LIXIONARY_JIRA_CLIENT_SECRET" \\`,
          `  -d grant_type=client_credentials -d resource=${endpoint} ${origin}/oauth/token`,
          '```',
          'Request a new token before it expires; in Claude Code, use a headersHelper script that prints',
          '`{"Authorization":"Bearer <token>"}` so every connection gets a fresh token.',
        ];
  return [
    'Set up the Lixionary Jira MCP server for me and learn how to use it.',
    '',
    '1. Add the server',
    ...add,
    '',
    '2. Authenticate',
    ...auth,
    '',
    '3. Check the connection',
    'Call list_projects and tell me which projects you can see. Then summarise the tools you have.',
    '',
    '4. Working rules (the server sends these too; always follow them)',
    instructions,
  ].join('\n');
}
